import { Component, ViewChild, ViewChildren, QueryList, effect, input, signal, NgZone, untracked } from '@angular/core';
import { take } from 'rxjs/operators';
import { GraphsBaseComponent } from '../../graphs-base.component';
import { Utils } from '../../../core/utils';
import { ChartComponent } from 'ng-apexcharts';
import {
  DataTakerStaffDataModel,
  GraphAnnotationModel,
  GraphAnnotationType,
  GraphDataModel,
  GraphGrouping,
  GraphSeriesModel,
  PatientProgramModel,
  PatientTargetModel,
  PatientTargetStatus,
  ProgramGraphType,
  TargetGraphType,
  TargetType,
} from '../../../core/api/models';
import { DatePipe } from '@angular/common';
import { GraphApiService } from '../../../core/api/services/graph-api.service';
import { AlertService } from '../../../shared/services/alert.service';
import { StaffApiService } from '../../../core/api/services/staff-api.service';
import { SupervisorApiService } from '../../../core/api/services/supervisor-api.service';
import { SelectItem } from 'primeng/api';
import { AuthService } from '../../../auth/auth.service';
import { Permissions } from '../../../core/api/models';
import { SeriesDataLabelOption, TrendLineDataLabelOption, AnnotationLabelBackgroundOption } from '../../graphs-base.component';
import { PrintService, PrintHeaderInfo } from '../../../shared/services/print.service';
@Component({
  selector: 'app-program-graph',
  templateUrl: './program-graph.component.html',
  styleUrl: './program-graph.component.css',
  standalone: false,
})
export class ProgramGraphComponent extends GraphsBaseComponent {
  @ViewChild('chart', { static: false }) chart: ChartComponent | undefined;
  @ViewChildren(ChartComponent) chartList!: QueryList<ChartComponent>;

  showAddAnnotation = signal<boolean>(false);
  annotationToEdit = signal<GraphAnnotationModel | null>(null);

  patientID = input.required<string>();
  patientIDAsNumber: number | null = null;

  startDate = signal<Date>(Utils.dateInPastBy(90));
  endDate = signal<Date>(new Date());
  forceGraphPeriod = signal<boolean>(true);

  isLoading: boolean = false;

  selectedProgram = input.required<PatientProgramModel>();

  selectedTargets: PatientTargetModel[] = [];

  targetTypes: SelectItem[] = [
    {
      label: 'Anecdotal',
      value: TargetType.Anecdotal,
    },
    {
      label: 'Discrete Trial Teaching',
      value: TargetType.DiscreteTrialTeaching,
    },
    {
      label: 'Conditional Discrimination',
      value: TargetType.ConditionalDiscrimination,
    },
    {
      label: 'Duration',
      value: TargetType.Duration,
    },
    {
      label: 'Echoic',
      value: TargetType.Echoic,
    },
    {
      label: 'Fluency Rate',
      value: TargetType.FluencyRate,
    },
    {
      label: 'Rate',
      value: TargetType.Rate,
    },
    {
      label: 'Frequency',
      value: TargetType.Frequency,
    },
    {
      label: 'Grouped Frequency',
      value: TargetType.GroupedFrequency,
    },
    {
      label: 'Jump To',
      value: TargetType.JumpTo,
    },
    {
      label: 'Currency',
      value: TargetType.Currency,
    },
    {
      label: 'Quantity',
      value: TargetType.Quantity,
    },
    {
      label: 'Intensity',
      value: TargetType.Intensity,
    },
    {
      label: 'Task Analysis',
      value: TargetType.TaskAnalysis,
    },
    {
      label: 'Toileting',
      value: TargetType.Toileting,
    },
    {
      label: 'Whole Partial Interval',
      value: TargetType.WholePartialInterval,
    },
  ];
  selectedTargetType: TargetType | null = null;

  targetStatuses: SelectItem[] = [
    {
      label: 'Ready',
      value: PatientTargetStatus.Ready,
    },
    {
      label: 'Baseline',
      value: PatientTargetStatus.Baseline,
    },
    {
      label: 'Aquisition',
      value: PatientTargetStatus.Aquisition,
    },
    {
      label: 'Mastered',
      value: PatientTargetStatus.Mastered,
    },
    {
      label: 'Maintenance',
      value: PatientTargetStatus.Maintenance,
    },
    {
      label: 'Closed',
      value: PatientTargetStatus.Closed,
    },
    {
      label: 'On Hold',
      value: PatientTargetStatus.On_Hold,
    },
    {
      label: 'Fluent',
      value: PatientTargetStatus.Fluent,
    },
  ];
  selectedTargetStatus: PatientTargetStatus | null = null;

  staffMembers = signal<DataTakerStaffDataModel[]>([]);
  selectedStaffMember: DataTakerStaffDataModel | null = null;

  graphData = signal<GraphDataModel | null>(null);

  programGraphTypes: any[] = [];
  selectedProgramGraphType: ProgramGraphType | null = null;


  seriesDataLabelOption = signal<SeriesDataLabelOption>(SeriesDataLabelOption.DotsAndValues);
  trendLineDataLabelOption = signal<TrendLineDataLabelOption>(TrendLineDataLabelOption.LineOnly);
  
  seriesDataLabelOptions = [
    { label: SeriesDataLabelOption.LineOnly, value: SeriesDataLabelOption.LineOnly },
    { label: SeriesDataLabelOption.Dots, value: SeriesDataLabelOption.Dots },
    { label: SeriesDataLabelOption.DotsAndValues, value: SeriesDataLabelOption.DotsAndValues },
    { label: SeriesDataLabelOption.FirstAndLastValues, value: SeriesDataLabelOption.FirstAndLastValues },
  ];
  
  trendLineDataLabelOptions = [
    { label: TrendLineDataLabelOption.LineOnly, value: TrendLineDataLabelOption.LineOnly },
    { label: TrendLineDataLabelOption.Dots, value: TrendLineDataLabelOption.Dots },
    { label: TrendLineDataLabelOption.DotsAndValues, value: TrendLineDataLabelOption.DotsAndValues },
    { label: TrendLineDataLabelOption.FirstAndLastValues, value: TrendLineDataLabelOption.FirstAndLastValues },
  ];
  displayLegend = signal<boolean>(true);

  displayTrendLine = signal<boolean>(false);
  segmentTrendLineByPhase = signal<boolean>(false);
  groupSubTargetsTogether = signal<boolean>(true);

  includeArchivedTargets = signal<boolean>(false);
  includeClosedTargets = signal<boolean>(false);
  includeOnHoldTargets = signal<boolean>(false);
  private lastAutoIncludeProgramId: number | null = null;
  private static instanceSeq = 0;
  private readonly programGraphInstanceId = `program-graph-${++ProgramGraphComponent.instanceSeq}`;
  private syncedChartGeneration = 0;
  private graphLoadRequestId = 0;

  private dateChangeTimer: any;

  isPrintingPdf = false;
  isPrintingPng = false;
  private readonly groupedExportPreparationDelayMs = 7000;
  private readonly syncedExportPreparationDelayMs = 2000;

  constructor(
    private graphApiService: GraphApiService,
    private supervisorApiService: SupervisorApiService,
    private staffApiService: StaffApiService,
    private datePipe: DatePipe,
    private alertService: AlertService,
    private authService: AuthService,
    private printService: PrintService,
    private ngZone: NgZone
  ) {
    super();
    this.graphExportService = printService;
    this.loadDefaultGraphOptions();
    this.isGrouped.set(false);

    // Set default date option to 3 months (index 6)
    this.dateOption = this.dateOptions[6].value;

    // Track only the selected program so include-toggle signal reads inside loadData
    // do not re-run this effect (checkbox handlers already reload the graph once).
    effect(() => {
      const programId = this.selectedProgram()?.ID;
      console.log('programId changed to:', programId ?? 'All Programs');
      untracked(() => {
        if (this.selectedProgramGraphType == null) {
          this.selectedProgramGraphType = ProgramGraphType.AllTargets;
        }
        this.applyAutoIncludeTogglesForProgram();
        this.loadData();
      });
    });
  }

  ngOnInit(): void {

    this.groupingOptions = this.groupingOptionsIncludingStaffOption;
    this.groupingOption = this.groupingOptions[2].value; // Daily

    this.patientIDAsNumber = Number(this.patientID());
    this.isLoading = true;
  }

  loadData() {
    this.loadDataForGraph();
  }

  onTargetTypeChange(event: any) {
    this.selectedTargetType = event.value;
    this.loadDataForGraph();
  }

  onTargetStatusChange(event: any) {
    this.selectedTargetStatus = event.value;
    this.loadDataForGraph();
  }

  onProgramGraphTypeChange(event: any) {
    this.selectedProgramGraphType = event.value;
    if (this.selectedProgramGraphType === ProgramGraphType.AllTargets) {
      this.applyAutoIncludeTogglesForProgram(true);
    }
    this.loadDataForGraph();
  }

  /**
   * Targets Mastered Cumulative is a single series and must not join ApexCharts
   * synced groups left over from All Targets / Acquisition charts.
   */
  shouldUseSingleChartLayout(): boolean {
    return (
      this.isGrouped() ||
      this.selectedProgramGraphType === ProgramGraphType.TargetsMasteredCumulative
    );
  }

  shouldDisplayIncludeTargetToggles(): boolean {
    return this.selectedProgramGraphType === ProgramGraphType.AllTargets;
  }

  includeAllTargets(): boolean {
    return (
      this.includeArchivedTargets() &&
      this.includeClosedTargets() &&
      this.includeOnHoldTargets()
    );
  }

  onIncludeArchivedTargetsChange(checked: boolean) {
    this.includeArchivedTargets.set(checked);
    this.loadDataForGraph();
  }

  onIncludeClosedTargetsChange(checked: boolean) {
    this.includeClosedTargets.set(checked);
    this.loadDataForGraph();
  }

  onIncludeOnHoldTargetsChange(checked: boolean) {
    this.includeOnHoldTargets.set(checked);
    this.loadDataForGraph();
  }

  onIncludeAllTargetsChange(checked: boolean) {
    this.includeArchivedTargets.set(checked);
    this.includeClosedTargets.set(checked);
    this.includeOnHoldTargets.set(checked);
    this.loadDataForGraph();
  }

  private applyAutoIncludeTogglesForProgram(force: boolean = false) {
    const program = this.selectedProgram();
    const programId = program?.ID ?? null;

    if (!force && programId === this.lastAutoIncludeProgramId) {
      return;
    }

    this.lastAutoIncludeProgramId = programId;

    const targets = (program?.Targets ?? []).filter(
      (target) => !target.IsDeleted
    );

    if (targets.length === 0) {
      this.includeArchivedTargets.set(false);
      this.includeClosedTargets.set(false);
      this.includeOnHoldTargets.set(false);
      return;
    }

    const normallyVisibleTargets = targets.filter(
      (target) =>
        !target.IsArchived &&
        target.TargetStatus !== PatientTargetStatus.Closed &&
        target.TargetStatus !== PatientTargetStatus.On_Hold
    );

    if (normallyVisibleTargets.length > 0) {
      this.includeArchivedTargets.set(false);
      this.includeClosedTargets.set(false);
      this.includeOnHoldTargets.set(false);
      return;
    }

    this.includeArchivedTargets.set(targets.some((target) => !!target.IsArchived));
    this.includeClosedTargets.set(
      targets.some((target) => target.TargetStatus === PatientTargetStatus.Closed)
    );
    this.includeOnHoldTargets.set(
      targets.some(
        (target) => target.TargetStatus === PatientTargetStatus.On_Hold
      )
    );
  }

  hasSupervisorPermission(): boolean {
    const userInfo = this.authService.userInfo;
    if (!userInfo || !userInfo.Permissions) {
      return false;
    }

    return userInfo.Permissions.includes(Permissions.SparkApp_Supervisor);
  }

  shouldDisplayAddAnnotationButton(): boolean {
    return this.hasSupervisorPermission();
  }

  updateAvailableProgramGraphTypes() {
    this.programGraphTypes = [
      {
        text: 'All Targets (excl. archived/closed/on hold)',
        value: ProgramGraphType.AllTargets,
      },
      {
        text: 'Filter By Target Type',
        value: ProgramGraphType.FilteredByTargetType,
      },
      {
        text: 'Filter By Target Status',
        value: ProgramGraphType.FilteredByTargetStatus,
      },
      {
        text: 'Targets Mastered Cumulative',
        value: ProgramGraphType.TargetsMasteredCumulative,
      },
    ];
  }

  loadDataForGraph() {
    this.updateAvailableProgramGraphTypes();
    this.bumpSyncedChartGroup();

    var startTime = this.startDate();
    var endTime = this.endDate();

    const requestId = ++this.graphLoadRequestId;
    this.isLoading = true;

    this.graphApiService
      .GetProgramGraph({
        patientID: Number(this.patientID()),
        programID: this.selectedProgram()?.ID ?? 0,
        targetIDs: this.selectedTargets?.map((target) => target.ID!),
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        programGraphType: this.selectedProgramGraphType!,
        targetTypeToFilter: this.selectedTargetType ?? undefined,
        targetStatusToFilter: this.selectedTargetStatus ?? undefined,
        graphGrouping: this.groupingOption,
        staffID: this.selectedStaffMember?.StaffID,
        includeTrendLine:
          this.selectedProgramGraphType !== ProgramGraphType.TargetsMasteredCumulative &&
          this.displayTrendLine(),
        segmentTrendLineByPhase:
          this.selectedProgramGraphType !== ProgramGraphType.TargetsMasteredCumulative &&
          this.displayTrendLine() &&
          this.segmentTrendLineByPhase(),
        includeArchivedTargets:
          this.selectedProgramGraphType === ProgramGraphType.AllTargets &&
          this.includeArchivedTargets(),
        includeClosedTargets:
          this.selectedProgramGraphType === ProgramGraphType.AllTargets &&
          this.includeClosedTargets(),
        includeOnHoldTargets:
          this.selectedProgramGraphType === ProgramGraphType.AllTargets &&
          this.includeOnHoldTargets(),
      })
      .subscribe({
        next: (data) => {
          if (requestId !== this.graphLoadRequestId) {
            return;
          }

          console.log(data);

          try {
            this.staffMembers.set(data.DataTakers ?? []);

            // Add placeholder data points for the start and end of the selected period
            if (this.forceGraphPeriod()) {
              const startDate = this.startDate().toISOString();
              const endDate = this.endDate().toISOString();

              if (data.Series && data.Series.length > 0) {
                for (const series of data.Series) {
                  if (series.DataPoints && series.DataPoints.length > 0) {
                    const firstDataPoint = series.DataPoints[0];
                    const lastDataPoint =
                      series.DataPoints[series.DataPoints.length - 1];

                    if (firstDataPoint.Date !== startDate) {
                      series.DataPoints.unshift({
                        Date: startDate,
                        Value: null,
                      });
                    }

                    if (lastDataPoint.Date !== endDate) {
                      series.DataPoints.push({ Date: endDate, Value: null });
                    }
                  } else {
                    // If no points in series, add placeholders for the entire period
                    series.DataPoints = [
                      { Date: startDate, Value: null },
                      { Date: endDate, Value: null },
                    ];
                  }
                }
              }
            }

            this.graphData.set(data);
            this.annotations.set(data.Annotations ?? []);
            this.updateAnnotationDisplayGroupCheckmarks();
            this.updateGraph();
          } catch (error) {
            console.error('Error rendering program graph', error);
            this.alertService.error(
              'Error rendering program graph - ' +
                Utils.getErrorMessageFromErrorResponse(error as any)
            );
            this.graphData.set(null);
          } finally {
            if (requestId === this.graphLoadRequestId) {
              this.isLoading = false;
            }
          }
        },
        error: (error) => {
          if (requestId !== this.graphLoadRequestId) {
            return;
          }

          console.error('Error fetching program graph', error);
          this.alertService.error(
            Utils.getErrorMessageFromErrorResponse(error)
          );
          this.graphData.set(null);
          this.isLoading = false;
        },
      });
  }

  shouldDisplayTargetTypeFilterOptions(): boolean {
    return (
      this.selectedProgramGraphType == ProgramGraphType.FilteredByTargetType
    );
  }

  shouldDisplayTargetStatusFilterOptions(): boolean {
    return (
      this.selectedProgramGraphType == ProgramGraphType.FilteredByTargetStatus
    );
  }

  shouldDisplayTrendLineOption(): boolean {
    return (
      this.selectedProgramGraphType !=
      ProgramGraphType.TargetsMasteredCumulative
    );
  }

  shouldDisplayGroupingOptions(): boolean {
    return (
      this.selectedProgramGraphType !=
      ProgramGraphType.TargetsMasteredCumulative
    );
  }

  shouldDisplayStaffMemberOptions(): boolean {
    return (
      this.staffMembers.length > 1 &&
      this.groupingOption != GraphGrouping.ByStaffMember
    );
  }

  shouldDisplayDisplayOptions(): boolean {
    return true;
  }

  shouldDisplayGraph(): boolean {
    return (
      this.isLoading === false &&
      this.selectedProgram != null &&
      !this.isGraphEmpty()
    );
  }

  shouldDisplayToggleGrouped(): boolean {
    return this.selectedProgramGraphType != ProgramGraphType.TargetsMasteredCumulative && this.groupingOption != GraphGrouping.ByStaffMember;
  }

  shouldDisplayGroupSubTargetsTogether(): boolean {
    return this.selectedProgramGraphType != ProgramGraphType.TargetsMasteredCumulative
      && this.groupingOption != GraphGrouping.ByStaffMember
      && !this.isGrouped();
  }

  isGraphEmpty(): boolean {
    // check if the graph data is empty
    if (this.graphData() == null) {
      return true;
    }

    // check if series is empty
    if (this.graphData()?.Series?.length == 0) {
      return true;
    }

    return false;
  }

  formatDateForInput(date: Date): string {
    return Utils.formatDateForInput(date);
  }

  onStartDateChange(event: any) {
    if (!event) return;

    // Clear any existing timer
    if (this.dateChangeTimer) {
      clearTimeout(this.dateChangeTimer);
    }

    // Set new timer
    this.dateChangeTimer = setTimeout(() => {
      const date = Utils.parseDateFromInput(event);
      this.startDate.set(date);
      this.dateOption = this.dateOptions[0].value; // Custom option value
      this.loadDataForGraph();
    }, 1500); // Wait 1.5 seconds after last change
  }

  onEndDateChange(event: any) {
    if (!event) return;

    // Clear any existing timer
    if (this.dateChangeTimer) {
      clearTimeout(this.dateChangeTimer);
    }

    // Set new timer
    this.dateChangeTimer = setTimeout(() => {
      const date = Utils.parseDateFromInput(event);
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);
      this.endDate.set(endOfDay);
      this.dateOption = this.dateOptions[0].value; // Custom option value
      this.loadDataForGraph();
    }, 1500); // Wait 1.5 seconds after last change
  }

  onDateChange(event: any) {
    const selectedDate = event.target.valueAsDate;
    // date is coming in UTC, we need to convert it to local time
    const localDate = new Date(
      selectedDate.getTime() + selectedDate.getTimezoneOffset() * 60000
    );
    const localDateEndOfDay = new Date(
      localDate.getTime() +
        23 * 60 * 60 * 1000 +
        59 * 60 * 1000 +
        59 * 1000 +
        999
    );

    this.startDate.set(localDate);
    this.endDate.set(localDateEndOfDay);
    //this.loadDataForGraph();
  }

  onDateOptionChange(event: any) {
    // Don't update if custom is selected
    if (event.value === this.dateOptions[0].value) {
      return;
    }

    // Update start date based on the selected option
    this.startDate.set(event.value);

    // Set end date to end of day today
    const endOfDayToday = new Date(
      new Date().getTime() +
        23 * 60 * 60 * 1000 +
        59 * 60 * 1000 +
        59 * 1000 +
        999
    );
    this.endDate.set(endOfDayToday);

    this.loadDataForGraph();
  }

  onGroupingOptionChange(event: any) {
    this.groupingOption = event.value;

    if (this.groupingOption == GraphGrouping.ByStaffMember) {
      this.selectedStaffMember = null;
    }

    this.loadDataForGraph();
  }

  onForceGraphPeriodChange(checked: boolean) {
    this.forceGraphPeriod.set(checked);
    this.loadDataForGraph();
  }

  onStaffMemberChange(event: any) {
    this.selectedStaffMember = event.value;
    this.loadDataForGraph();
  }

  override getDataLabelsConfig(): any {
    const seriesOption = this.seriesDataLabelOption();
    const trendLineOption = this.trendLineDataLabelOption();
    const seriesCount = this.graphData()?.Series?.length ?? 0;
    const hasTrendLine =
      this.displayTrendLine() && seriesCount > 0 && seriesCount <= 2;

    return this.buildSeriesTrendDataLabelsConfig(seriesOption, trendLineOption, {
      hasTrendLine,
      seriesCount: Math.max(seriesCount, 1),
      alternateTrendLines: false,
    });
  }

  getEnabledOnSeries(seriesOption: SeriesDataLabelOption, trendLineOption: TrendLineDataLabelOption): number[] {
    const enabled: number[] = [];
    
    // Series (index 0) - enabled if not LineOnly
    if (seriesOption !== SeriesDataLabelOption.LineOnly) {
      enabled.push(0);
    }
    
    // Trend line (index 1) - enabled if not LineOnly and trend line is displayed
    if (this.displayTrendLine() && trendLineOption !== TrendLineDataLabelOption.LineOnly) {
      enabled.push(1);
    }
    
    return enabled;
  }

  override getMarkersConfig(): any {
    const seriesOption = this.seriesDataLabelOption();
    const trendLineOption = this.trendLineDataLabelOption();
    const series = this.graphData()?.Series ?? [];
    const seriesCount = series.length;
    const hasTrendLine =
      this.displayTrendLine() && seriesCount > 0 && seriesCount <= 2;
    const graphData = this.graphData();
    const seriesDataList = graphData
      ? series.map((s) => this.processSeriesDataWithBreaks(s, graphData).data)
      : [];
    const colors = this.resolveChartColors(series);

    return this.buildSeriesTrendMarkersConfig(seriesOption, trendLineOption, {
      hasTrendLine,
      seriesCount: Math.max(seriesCount, 1),
      alternateTrendLines: false,
      seriesDataList,
      colors,
    });
  }

  // For multi-series charts where we know the exact number of series (e.g., grouped by staff per target),
  // adapt markers to apply selection consistently across all series in that chart
  private getMarkersConfigForSeriesCount(seriesCount: number): any {
    const seriesOption = this.seriesDataLabelOption();
    const trendLineOption = this.trendLineDataLabelOption();
    const hasTrendLine = this.displayTrendLine();
    const graphData = this.graphData();
    const series = graphData?.Series ?? [];
    const seriesDataList = graphData
      ? series.slice(0, seriesCount).map((s) => this.processSeriesDataWithBreaks(s, graphData).data)
      : [];
    const colors = this.resolveChartColors(series).slice(0, seriesCount);

    return this.buildSeriesTrendMarkersConfig(seriesOption, trendLineOption, {
      hasTrendLine,
      seriesCount,
      alternateTrendLines: hasTrendLine,
      seriesDataList,
      colors,
    });
  }

  // For multi-series charts where we know the exact number of series, adapt data labels configuration
  private getDataLabelsConfigForSeriesCount(seriesCount: number): any {
    const seriesOption = this.seriesDataLabelOption();
    const trendLineOption = this.trendLineDataLabelOption();
    const hasTrendLine = this.displayTrendLine();

    return this.buildSeriesTrendDataLabelsConfig(seriesOption, trendLineOption, {
      hasTrendLine,
      seriesCount,
      alternateTrendLines: hasTrendLine,
    });
  }

  shouldDisplayAnnotationLabelBackgroundOption(): boolean {
    return true;
  }

  onAnnotationLabelBackgroundOptionSelected(value: AnnotationLabelBackgroundOption): void {
    this.annotationLabelBackgroundOption.set(value);
    this.onAnnotationLabelBackgroundOptionChange();
  }

  onDeclutterAnnotationsSelected(value: boolean): void {
    this.declutterAnnotations.set(value);
    this.onDeclutterAnnotationsChange();
  }

  onAnnotationLabelPositionChange(
    annotation: GraphAnnotationModel,
    labelPosition: 'start' | 'middle' | 'end'
  ): void {
    this.applyAnnotationLabelPositionChange(
      annotation,
      labelPosition,
      this.graphData(),
      (value) => this.graphData.set(value),
      (updated) => {
        this.graphApiService.SaveGraphAnnotation({ body: updated }).subscribe({
          next: () => this.refreshChartAnnotations(),
          error: (error) =>
            this.alertService.error(
              'Error saving annotation - ' + Utils.getErrorMessageFromErrorResponse(error)
            ),
        });
      }
    );
  }

  getAnnotationListItems() {
    return this.getVisibleAnnotationListItems(this.startDate(), this.endDate());
  }

  onDisplayLegendChange() {
    this.updateGraph();
  }

  onToggleGrouped() {
    this.bumpSyncedChartGroup();
    this.updateGraph();
  }

  onToggleGroupSubTargetsTogether() {
    this.bumpSyncedChartGroup();
    this.updateGraph();
  }

  onToggleTrendLine() {
    this.loadDataForGraph();
  }

  onToggleSegmentTrendLineByPhase() {
    this.loadDataForGraph();
    }

  override updateGraph() {
    console.log('updateGraph start - ' + new Date().toISOString());
    if (!this.graphData()) {
      // hide graph
      this.chartOptions.series = [];
      return;
    }
    switch (this.selectedProgramGraphType) {
      case ProgramGraphType.TargetsMasteredCumulative:
        this.loadDefaultLineGraphOptionsForPrograms(
          this.graphData()!, 
          this.seriesDataLabelOption(), 
          this.displayLegend(), 
          this.shouldRenderAnnotations(), 
          false,
          false,
          false,
          false,
          true
        );
        break;
      case ProgramGraphType.AllTargets:
      case ProgramGraphType.FilteredByTargetType:
      case ProgramGraphType.FilteredByTargetStatus:
      case ProgramGraphType.SelectedTargets:
        // check if the selected targets are all prompt based to set the y axis min and max to 0 and 100
        const allPromptBased = this.selectedTargets.every(target => target.TargetType == TargetType.Echoic || target.TargetType == TargetType.DiscreteTrialTeaching);
        const hasTextValues = this.graphData()?.Series?.some(series => series.DataPoints?.some(dataPoint => !!dataPoint.TextValue)) ?? false;
        this.loadDefaultLineGraphOptionsForPrograms(this.graphData()!, this.seriesDataLabelOption(), this.displayLegend(), this.shouldRenderAnnotations(), this.displayTrendLine(), allPromptBased, false, true, false, hasTextValues);
        break;
      default:
        break;
    }

    // Set a default filename immediately to prevent random filename generation
    this.setDefaultExportFilename();

    this.applyChartOptions(this.chart, this.chartOptions);
    if (!this.shouldUseSingleChartLayout() && this.chartList?.length) {
      this.chartList.forEach((syncedChart, index) => {
        const syncedOptions = this.chartsOptions[index];
        if (syncedOptions) {
          this.applyChartOptions(syncedChart, syncedOptions);
        }
      });
    }

    console.log('updateGraph end - ' + new Date().toISOString());
  }

  private setDefaultExportFilename(): void {
    // Set a basic filename immediately to prevent ApexCharts from generating a random one
    const graphType = this.getProgramGraphTypeDisplayName() || 'Graph';
    const programName = this.selectedProgram()?.ProgramName || 'Program';
    const defaultFilename = `${graphType}-${programName}`.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '_').substring(0, 100);
    
    if (this.chartOptions.chart?.toolbar?.export) {
      if (!this.chartOptions.chart.toolbar.export.csv) {
        this.chartOptions.chart.toolbar.export.csv = {};
      }
      if (!this.chartOptions.chart.toolbar.export.svg) {
        this.chartOptions.chart.toolbar.export.svg = {};
      }
      if (!this.chartOptions.chart.toolbar.export.png) {
        this.chartOptions.chart.toolbar.export.png = {};
      }
      this.chartOptions.chart.toolbar.export.csv.filename = defaultFilename;
      this.chartOptions.chart.toolbar.export.svg.filename = defaultFilename;
      this.chartOptions.chart.toolbar.export.png.filename = defaultFilename;
    }
  }

  onSeriesDataLabelOptionChange() {
    this.updateGraph();
  }

  onTrendLineDataLabelOptionChange() {
    this.updateGraph();
  }

  // Annotation functions

  addAnnotationButtonClicked() {
    console.log('addAnnotationButtonClicked');
    const emptyAnnotation: GraphAnnotationModel = {
      PatientProgramIDs: [this.selectedProgram()!.ID!],
    };
    this.annotationToEdit.set(emptyAnnotation);
    this.showAddAnnotation.set(true);
  }

  editAnnotation(annotation: GraphAnnotationModel) {
    console.log('editAnnotation', annotation);
    this.annotationToEdit.set(annotation);
    this.showAddAnnotation.set(true);
  }

  onCloseAddAnnotation(withChanges: boolean = false) {
    console.log('onCloseAddAnnotation', withChanges);
    if (withChanges) {
      this.loadData();
    }
    this.showAddAnnotation.set(false);
  }

  // graph functions
  loadDefaultLineGraphOptionsForPrograms(
    graphData: GraphDataModel, 
    displayDataPointOption: SeriesDataLabelOption = SeriesDataLabelOption.DotsAndValues, 
    displayLegend: boolean = true, 
    displayAnnotations: boolean = true, 
    displayTrendLineForProgramGraph: boolean = false,
    limitYAxisValues: boolean = false,
    displayTotalSessionTimeAndTargetDataTimeInTooltip: boolean = false,
    displayPromptDataInTooltip: boolean = false,
    displayMasteredTargetsTooltip: boolean = false,
    displayTextValueTooltip: boolean = false
  ) {
    if (this.shouldUseSingleChartLayout()) {
      // Grouped Graph or Normal Graphs
      console.log('Program Graph - Grouped Graph');
      this.chartsOptions = [];
      this.chartOptions = {
        series: graphData.Series!.map((series, index) =>
          this.mapLineSeriesForChart(series, graphData, index)
        ),
        colors: this.resolveChartColors(graphData.Series),
        legend: this.buildLegendConfig(displayLegend),
        chart: {
          height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
          width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
          type: 'line',
          ...this.sharedChartConfig,
          events: this.createAnnotationReorderEvent(),
        },
        dataLabels: this.getDataLabelsConfig(),
        stroke: {
          curve: 'straight',
        },
        grid: {
          padding: {
            right: 30,
            left: 20,
          },
        },
        markers: this.getMarkersConfig(),
        tooltip: displayMasteredTargetsTooltip ? { custom: this.createCustomTooltipMasteredTargets(graphData) } : displayTextValueTooltip ? {
          custom: this.createCustomTooltipTextValue(graphData, this.staffMembers())
        } : displayTotalSessionTimeAndTargetDataTimeInTooltip || displayPromptDataInTooltip ? {
          custom: displayPromptDataInTooltip ? this.createCustomTooltipPrompt(graphData, this.staffMembers()) : this.createCustomTooltipTime(graphData)
        } : {
          y: {
            formatter: (value: any) => {
              if (value === null) {
                return '';
              }
              return value;
            },
          },
        },
        title: {
          text: graphData.Title ?? 'Chart',
        },
        xaxis: {
          type: 'datetime',
          title: {
            text: graphData.XAxisTitle ?? 'X Axis',
          },
          labels: {
            datetimeUTC: false,
          },
        },
        yaxis: {
          min: limitYAxisValues ? 0 : undefined,
          max: limitYAxisValues ? 100 : undefined,
          title: {
            text: graphData.YAxisTitle ?? 'Y Axis',
          },
          labels: {
            formatter: (value: number) => {
              // If it's a whole number, don't show decimal places
              if (Number.isInteger(value)) {
                return value.toString();
              }
              // If it's a decimal, round to 2 decimal places
              return value.toFixed(2);
            }
          }
        },
        annotations: this.buildAnnotationsConfig(graphData, null, displayAnnotations),
      };
    } else {
      // Synced Graphs
      const syncedGroupId = this.syncedChartGroupId();

      // Group charts by parent target when viewing by staff, or when clumping TA/GF/CD sub-targets.
      if (this.groupingOption === GraphGrouping.ByStaffMember || this.groupSubTargetsTogether()) {
        console.log('Program Graph - Synced Graphs - Group by PatientTargetID');
        const seriesByTargetId = new Map<number | string, GraphSeriesModel[]>();
        let ungroupedIndex = 0;
        (graphData.Series || []).forEach((s) => {
          const key = s.PatientTargetID != null ? s.PatientTargetID : `ungrouped-${ungroupedIndex++}`;
          const arr = seriesByTargetId.get(key) || [];
          arr.push(s);
          seriesByTargetId.set(key, arr);
        });
        this.chartsOptions = Array.from(seriesByTargetId.entries()).map(([targetId, seriesList]) => {
          const targetName = seriesList[0].PatientTargetName ?? seriesList[0].Name ?? 'Chart';
          return {
            series: seriesList.map((s) => ({
              name: s.Name ?? 'No Name',
              data: this.processSeriesDataWithBreaks(s, graphData).data,
            })),
            legend: displayLegend ? {
              position: 'top',
              onItemClick: { toggleDataSeries: true },
              onItemHover: { highlightDataSeries: true },
            } : { show: false },
            chart: {
              id: this.syncedChartId(`target-${targetId}`),
              group: syncedGroupId,
              height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
              width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
              type: 'line',
              ...this.sharedChartConfig,
              events: this.createAnnotationReorderEvent(),
            },
            dataLabels: this.getDataLabelsConfigForSeriesCount(seriesList.length),
            stroke: { curve: 'straight' },
            grid: { padding: { right: 30, left: 20 } },
            markers: this.getMarkersConfigForSeriesCount(seriesList.length),
            tooltip: displayMasteredTargetsTooltip ? { custom: this.createCustomTooltipMasteredTargets(graphData) } : displayTextValueTooltip ? {
              custom: this.createCustomTooltipTextValue(graphData, this.staffMembers())
            } : (displayTotalSessionTimeAndTargetDataTimeInTooltip || displayPromptDataInTooltip) ? {
              custom: displayPromptDataInTooltip ? this.createCustomTooltipPrompt(graphData, this.staffMembers()) : this.createCustomTooltipTime(graphData)
            } : {
              x: { format: 'MMM dd yyyy' },
              y: {
                formatter: (value: any) => (value === null ? '' : value),
              },
            },
            title: {
              text: targetName,
            },
            xaxis: {
              type: 'datetime',
              title: { text: graphData.XAxisTitle ?? 'X Axis' },
              labels: { datetimeUTC: false },
            },
            yaxis: {
              min: limitYAxisValues ? 0 : undefined,
              max: limitYAxisValues ? 100 : undefined,
              title: { text: graphData.YAxisTitle ?? 'Y Axis' },
              labels: {
                formatter: (value: number) => {
                  if (Number.isInteger(value)) {
                    return value.toString();
                  }
                  return value.toFixed(2);
                }
              }
            },
            annotations: this.buildAnnotationsConfig(
              graphData,
              seriesList[0] ?? null,
              displayAnnotations
            ),
          };
        });
        console.log('Program Graph - Synced Graphs - Grouped by TargetID - Charts Options', this.chartsOptions);
      }
      else {
        // if it is a program graph and trend line is selected, we need to graph types two by two;
        // every other graph in the series will be the trend line in this case;
        if (displayTrendLineForProgramGraph && (graphData.Series?.length ?? 0) > 1) { // Program Graph with Trend Line
          console.log('Program Graph with Trend Line');
          var allSeries = graphData.Series!;
          var notTrendLineSeries = allSeries.filter((series, index) => index % 2 === 0);
          var trendLineSeries = allSeries.filter((series, index) => index % 2 === 1);

        this.chartsOptions = notTrendLineSeries.map((series, seriesIndex) => {
          var trendLineSeriesForGraph = trendLineSeries.shift();
          const pairedSeries = [
              {
                name: series.Name ?? 'No Name',
                data: this.processSeriesDataWithBreaks(series, graphData).data,
              },
          ];
          if (trendLineSeriesForGraph) {
            pairedSeries.push({
                name: trendLineSeriesForGraph.Name ?? 'No Name',
                data: this.processSeriesDataWithBreaks(trendLineSeriesForGraph, graphData).data,
            });
          }
          return {
            series: pairedSeries,
            legend: displayLegend
              ? {
                  position: 'top',
                  onItemClick: {
                    toggleDataSeries: true,
                  },
                  onItemHover: {
                    highlightDataSeries: true,
                  },
                }
              : {
                  show: false,
                },
            chart: {
              id: this.syncedChartId(series.Name ?? `chart-${seriesIndex}`),
              group: syncedGroupId,
              height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
              width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
              type: 'line',
              ...this.sharedChartConfig,
              events: this.createAnnotationReorderEvent(),
            },
            dataLabels: this.getDataLabelsConfigForSeriesCount(pairedSeries.length),
            stroke: {
              curve: 'straight',
            },
            grid: {
              padding: {
                right: 30,
                left: 20,
              },
            },
            markers: this.getMarkersConfigForSeriesCount(pairedSeries.length),
            tooltip: displayMasteredTargetsTooltip ? { custom: this.createCustomTooltipMasteredTargets(graphData) } : displayTextValueTooltip ? {
              custom: this.createCustomTooltipTextValue(graphData, this.staffMembers())
            } : displayTotalSessionTimeAndTargetDataTimeInTooltip || displayPromptDataInTooltip ? {
              custom: displayPromptDataInTooltip ? this.createCustomTooltipPrompt(graphData, this.staffMembers()) : this.createCustomTooltipTime(graphData)
            } : {
              x: {
                format: 'MMM dd yyyy',
              },
              y: {
                formatter: (value: any) => {
                  if (value === null) {
                    return '';
                  }
                  return value;
                },
              },
            },
            title: {
              text: series.Name ?? 'Chart',
            },
            xaxis: {
              type: 'datetime',
              title: {
                text: graphData.XAxisTitle ?? 'X Axis',
              },
              labels: {
                datetimeUTC: false,
              },
            },
            yaxis: {
              min: limitYAxisValues ? 0 : undefined,
              max: limitYAxisValues ? 100 : undefined,
              title: {
                text: graphData.YAxisTitle ?? 'Y Axis',
                },
              labels: {
                formatter: (value: number) => {
                  // If it's a whole number, don't show decimal places
                  if (Number.isInteger(value)) {
                    return value.toString();
                  }
                  // If it's a decimal, round to 2 decimal places
                  return value.toFixed(2);
                }
              }
              },
              annotations: this.buildAnnotationsConfig(graphData, series, displayAnnotations),
          }
        });
      } else {
        // Synced Graphs
        console.log('Program Graph - Synced Graphs');
        this.chartsOptions = graphData.Series!.map((series, seriesIndex) => {
          return {
            series: [
              {
                name: series.Name ?? 'No Name',
                data: this.processSeriesDataWithBreaks(series, graphData).data,
              },
            ],
            legend: displayLegend
              ? {
                  position: 'top',
                  onItemClick: {
                    toggleDataSeries: true,
                  },
                  onItemHover: {
                    highlightDataSeries: true,
                  },
                }
              : {
                  show: false,
                },
            chart: {
              id: this.syncedChartId(series.Name ?? `chart-${seriesIndex}`),
              group: syncedGroupId,
              height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
              width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
              type: 'line',
              ...this.sharedChartConfig,
              events: this.createAnnotationReorderEvent(),
            },
            dataLabels: this.getDataLabelsConfig(),
            stroke: {
              curve: 'straight',
            },
            grid: {
              padding: {
                right: 30,
                left: 20,
              },
            },
            markers: this.getMarkersConfig(),
            tooltip: displayMasteredTargetsTooltip ? { custom: this.createCustomTooltipMasteredTargets(graphData) } : displayTextValueTooltip ? {
              custom: this.createCustomTooltipTextValue(graphData, this.staffMembers())
            } : displayTotalSessionTimeAndTargetDataTimeInTooltip || displayPromptDataInTooltip ? {
              custom: displayPromptDataInTooltip ? this.createCustomTooltipPrompt(graphData, this.staffMembers()) : this.createCustomTooltipTime(graphData)
            } : {
              x: {
                format: 'MMM dd yyyy',
              },
              y: {
                formatter: (value: any) => {
                  if (value === null) {
                    return '';
                  }
                  return value;
                },
              },
            },
            title: {
              text: series.Name ?? 'Chart',
            },
            xaxis: {
              type: 'datetime',
              title: {
                text: graphData.XAxisTitle ?? 'X Axis',
              },
              labels: {
                datetimeUTC: false,
              },
            },
            yaxis: {
              min: limitYAxisValues ? 0 : undefined,
              max: limitYAxisValues ? 100 : undefined,
              title: {
                text: graphData.YAxisTitle ?? 'Y Axis',
                },
              labels: {
                formatter: (value: number) => {
                  // If it's a whole number, don't show decimal places
                  if (Number.isInteger(value)) {
                    return value.toString();
                  }
                  // If it's a decimal, round to 2 decimal places
                  return value.toFixed(2);
                }
              }
              },
            annotations: this.buildAnnotationsConfig(graphData, series, displayAnnotations),
          }
        });
      }
      }
    }
  }

  /**
   * Formats annotation label text to use multiple lines if it exceeds 30 characters
   */
  protected override formatAnnotationLabelText(
    text: string
  ): string | string[] {
    if (text.length <= 30) {
      return text;
    }

    // Split the text into words and filter out empty strings
    const words = text.split(' ').filter((word) => word.length > 0);
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      // If adding this word would exceed 30 characters, start a new line
      if (currentLine.length + word.length + 1 > 30 && currentLine.length > 0) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        if (currentLine.length === 0) {
          currentLine = word;
        } else {
          currentLine += ' ' + word;
        }
      }
    }

    // Add the last line if it has content
    if (currentLine.length > 0) {
      lines.push(currentLine);
    }

    // Return array of strings for multiline support in ApexCharts
    // Pad each line to exactly 30 characters with whitespace
    if (lines.length > 1) {
      return lines.map((line) => line.padEnd(30, ' '));
    }

    return text;
  }

  /**
   * Handles print click from the graph toolbar
   */
  override handleDownloadPngClick(): void {
    this.setPngLoadingState(true);
    if (!this.graphData()) {
      this.setPngLoadingState(false);
      return;
    }

    this.waitForExportRender(this.shouldUseSingleChartLayout())
      .then(() => this.buildReportHeaderInfo())
      .then(async (header) => {
        const isGrouped = this.shouldUseSingleChartLayout();
        if (isGrouped) {
          if (!this.chart) return;
          await this.printService.downloadPngPage(this.chart as any, header);
        } else {
          const charts = this.chartList ? this.chartList.toArray() : [];
          if (charts.length === 0) return;
          await this.printService.downloadPngPageForCharts(charts as any, header);
        }
      })
      .finally(() => {
        this.setPngLoadingState(false);
      });
  }

  override handleDownloadPdfClick(): void {
    if (!this.graphData()) return;
    this.buildHeaderInfoAndDownloadPdf();
  }

  private async buildReportHeaderInfo(): Promise<PrintHeaderInfo> {
    let patientName = 'Unknown Patient';
    try {
      const patient = await this.staffApiService.GetPatientInfo({ id: Number(this.patientID()) }).pipe(take(1)).toPromise();
      if (patient) {
        patientName = `${patient.FirstName || ''} ${patient.LastName || ''}`.trim() || patient.PatientName || 'Unknown Patient';
      }
    } catch {}
    const startDate = this.startDate();
    const endDate = this.endDate();
    const timePeriod = `${this.datePipe.transform(startDate, 'MMM dd, yyyy')} - ${this.datePipe.transform(endDate, 'MMM dd, yyyy')}`;
    const graphType = this.getProgramGraphTypeDisplayName();
    const subtitleItems: string[] = [];
    if (this.selectedProgram()) subtitleItems.push(`${this.selectedProgram()!.ProgramName || 'Unnamed Program'}`);
    if (this.selectedTargets && this.selectedTargets.length > 0) subtitleItems.push(...this.selectedTargets.map(t => `${t.TargetName || 'Unnamed Target'} (${Utils.patientTargetTypeLabel(t.TargetType!)})`));
    const selectedTargets = this.selectedTargets.map(t => t.TargetName || 'Unknown Target');
    const visibleAnnotations = this.annotations()
      .filter(annotation => {
        if (!this.isAnnotationDisplayed(annotation)) return false;
        if (annotation.StartDate) {
          const annotationDate = new Date(annotation.StartDate);
          return annotationDate >= startDate && annotationDate <= endDate;
        }
        return true;
      })
      .map(annotation => (this.annotationLabel(annotation)));
    const headerInfo: PrintHeaderInfo = {
      graphTitle: this.graphData()?.Title || 'Program Graph',
      graphType,
      componentType: 'Program Graph',
      patientName,
      timePeriod,
      groupingLabel: this.groupingOptionLabel(),
      displayOptions: this.getDisplayOptions(),
      subtitleItems,
      selectedTargets,
      selectedProgram: this.selectedProgram()?.ProgramName || undefined,
      annotations: visibleAnnotations,
      annotationColorLegends: this.getDeclutterLegendItemsForExport(),
      staffMember: this.getStaffMember() ?? undefined
    };
    return headerInfo;
  }

  private groupingOptionLabel(): string {
    const groupingMap: { [key in GraphGrouping]: string } = {
      [GraphGrouping.BySession]: 'By Session',
      [GraphGrouping.Daily]: 'Daily',
      [GraphGrouping.Weekly]: 'Weekly',
      [GraphGrouping.Monthly]: 'Monthly',
      [GraphGrouping.Yearly]: 'Yearly',
      [GraphGrouping.ByStaffMember]: 'By Staff Member',
      [GraphGrouping.ByTrial]: 'By Trial',
    };
    return groupingMap[this.groupingOption];
  }

  private getDisplayOptions(): string[] {
    const options: string[] = [];
    if (this.shouldDisplayToggleGrouped()) {
      if (this.isGrouped()) options.push('Grouped'); else options.push('Synced');
    }
    if (this.shouldDisplayGroupSubTargetsTogether()) {
      options.push(this.groupSubTargetsTogether() ? 'Sub-targets grouped' : 'Sub-targets separate');
    }
    options.push(`Series: ${this.seriesDataLabelOption()}`);
    if (this.displayTrendLine()) {
      options.push(`Trend Line: ${this.trendLineDataLabelOption()}`);
    }
    if (this.shouldDisplayAnnotationLabelBackgroundOption()) {
      options.push(`Annotation Labels: ${this.annotationLabelBackgroundOption()}`);
    }
    if (this.declutterAnnotations()) {
      options.push('Declutter Annotations');
    }
    return options;
  }

  private getStaffMember(): string | undefined {
    return this.selectedStaffMember?.FullName ?? undefined;
  }

  private async buildHeaderInfoAndDownloadPdf(): Promise<void> {
    this.setPdfLoadingState(true);
    try {
      await this.waitForExportRender(this.shouldUseSingleChartLayout());
      const headerInfo: PrintHeaderInfo = await this.buildReportHeaderInfo();
      const isGrouped = this.shouldUseSingleChartLayout();
      if (isGrouped) {
        if (!this.chart) return;
        await this.printService.downloadPdf(this.chart as any, headerInfo);
      } else {
        const charts = this.chartList ? this.chartList.toArray() : [];
        if (charts.length === 0) return;
        await this.printService.downloadPdfForCharts(charts as any, headerInfo);
      }
    } finally {
      this.setPdfLoadingState(false);
    }
  }

  private setPngLoadingState(isLoading: boolean): void {
    this.ngZone.run(() => {
      this.isPrintingPng = isLoading;
      this.updateGraph();
    });
  }

  private setPdfLoadingState(isLoading: boolean): void {
    this.ngZone.run(() => {
      this.isPrintingPdf = isLoading;
      this.updateGraph();
    });
  }

  private waitForExportRender(isGrouped: boolean): Promise<void> {
    const delayMs = isGrouped
      ? this.groupedExportPreparationDelayMs
      : this.syncedExportPreparationDelayMs;

    return new Promise((resolve) => {
      setTimeout(() => resolve(), delayMs);
    });
  }

  private bumpSyncedChartGroup(): void {
    this.syncedChartGeneration += 1;
  }

  private syncedChartGroupId(): string {
    return `${this.programGraphInstanceId}-${this.syncedChartGeneration}`;
  }

  private syncedChartId(key: string): string {
    return `${this.syncedChartGroupId()}-${key}`;
  }

  /**
   * Gets the display name for the current program graph type
   */
  private getProgramGraphTypeDisplayName(): string {
    if (this.selectedProgramGraphType === null || this.selectedProgramGraphType === undefined) {
      return 'Unknown Graph Type';
    }

    const graphTypeMap: { [key in ProgramGraphType]: string } = {
      [ProgramGraphType.AllTargets]: 'All Targets (excl. archived/closed/on hold)',
      [ProgramGraphType.FilteredByTargetType]: 'Filtered by Target Type',
      [ProgramGraphType.FilteredByTargetStatus]: 'Filtered by Target Status',
      [ProgramGraphType.SelectedTargets]: 'Selected Targets',
      [ProgramGraphType.TargetsMasteredCumulative]: 'Targets Mastered Cumulative'
    };

    return graphTypeMap[this.selectedProgramGraphType] || 'Unknown Graph Type';
  }

}
